import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, CircularProgress, Button,
} from '@mui/material';
import { ArrowBack, LocationOn, AccessTime } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../services/authService';

interface CheckInDetail {
  id: number;
  user_id: number;
  building_id: number;
  check_in_time: string | null;
  check_out_time: string | null;
  check_in_notes: string | null;
  check_out_notes: string | null;
  check_in_location_lat: number | null;
  check_in_location_lng: number | null;
  check_out_location_lat: number | null;
  check_out_location_lng: number | null;
  before_images: string[] | null;
  after_images: string[] | null;
  building?: { id: number; name: string; name_ar?: string };
  user?: { id: number; name: string; name_ar?: string };
  amenities?: { id: number; name_en: string; name_ar?: string }[];
  selected_flats?: { id: number; flat_number: string }[];
}

const formatDT = (dt: string | null, lang: string) => {
  if (!dt) return '-';
  return new Date(dt).toLocaleString(lang === 'ar' ? 'ar' : 'en', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kuwait',
  });
};

const formatDuration = (start: string | null, end: string | null) => {
  if (!start) return '-';
  const s = new Date(start).getTime();
  const e = end ? new Date(end).getTime() : Date.now();
  const mins = Math.round((e - s) / 60000);
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
};

const QuickCheckInDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const [data, setData] = useState<CheckInDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/check-ins/${id}`);
        setData(res.data);
      } catch (e) {
        console.error(e);
        setData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  if (loading) return <Box textAlign="center" py={10}><CircularProgress /></Box>;
  if (!data) return <Box textAlign="center" py={10}><Typography color="error">Record not found</Typography></Box>;

  const buildingName = language === 'ar' && data.building?.name_ar ? data.building.name_ar : (data.building?.name || '-');
  const techName = language === 'ar' && data.user?.name_ar ? data.user.name_ar : (data.user?.name || '-');
  const isActive = data.check_in_time && !data.check_out_time;

  return (
    <Box>
      <Box display="flex" alignItems="center" gap={2} mb={3}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          {t('common.back') || 'Back'}
        </Button>
        <Typography variant="h4">{t('quickCheckIn.detail.title') || 'Quick Check-In Detail'}</Typography>
        <Chip
          label={isActive ? (t('quickCheckIn.detail.active') || 'Active') : (t('quickCheckIn.detail.completed') || 'Completed')}
          color={isActive ? 'success' : 'default'}
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, gap: 3 }}>
        {/* Main Info */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>{t('quickCheckIn.detail.info') || 'Information'}</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('quickCheckIn.detail.technician') || 'Technician'}</Typography>
                  <Typography fontWeight="bold">{techName}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('quickCheckIn.detail.building') || 'Building'}</Typography>
                  <Typography fontWeight="bold">{buildingName}</Typography>
                </Box>
                {data.selected_flats && data.selected_flats.length > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary">{t('quickCheckIn.detail.flats') || 'Flats'}</Typography>
                    <Typography fontWeight="bold">{data.selected_flats.map(f => f.flat_number).join(', ')}</Typography>
                  </Box>
                )}
                {data.amenities && data.amenities.length > 0 && (
                  <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary">{t('quickCheckIn.detail.amenities') || 'Amenities'}</Typography>
                    <Box display="flex" gap={0.5} flexWrap="wrap" justifyContent="flex-end">
                      {data.amenities.map(a => <Chip key={a.id} label={language === 'ar' && a.name_ar ? a.name_ar : a.name_en} size="small" />)}
                    </Box>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Time Info */}
        <Box>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom><AccessTime sx={{ verticalAlign: 'middle', mr: 1 }} />{t('quickCheckIn.detail.timing') || 'Timing'}</Typography>
              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('quickCheckIn.detail.checkInTime') || 'Check-In Time'}</Typography>
                  <Typography fontWeight="bold">{formatDT(data.check_in_time, language)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('quickCheckIn.detail.checkOutTime') || 'Check-Out Time'}</Typography>
                  <Typography fontWeight="bold">{formatDT(data.check_out_time, language)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">{t('quickCheckIn.detail.duration') || 'Duration'}</Typography>
                  <Typography fontWeight="bold">{formatDuration(data.check_in_time, data.check_out_time)}</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Location */}
        {data.check_in_location_lat && data.check_in_location_lng && (
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom><LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />{t('quickCheckIn.detail.checkInLocation') || 'Check-In Location'}</Typography>
                <iframe
                  title="check-in-location"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: 8 }}
                  loading="lazy"
                  src={`https://www.google.com/maps?q=${data.check_in_location_lat},${data.check_in_location_lng}&z=15&output=embed`}
                />
              </CardContent>
            </Card>
          </Box>
        )}

        {data.check_out_location_lat && data.check_out_location_lng && (
          <Box>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom><LocationOn sx={{ verticalAlign: 'middle', mr: 1 }} />{t('quickCheckIn.detail.checkOutLocation') || 'Check-Out Location'}</Typography>
                <iframe
                  title="check-out-location"
                  width="100%"
                  height="200"
                  style={{ border: 0, borderRadius: 8 }}
                  loading="lazy"
                  src={`https://www.google.com/maps?q=${data.check_out_location_lat},${data.check_out_location_lng}&z=15&output=embed`}
                />
              </CardContent>
            </Card>
          </Box>
        )}

        {/* Notes */}
        {(data.check_in_notes || data.check_out_notes) && (
          <Box sx={{ gridColumn: "1 / -1" }}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t('quickCheckIn.detail.notes') || 'Notes'}</Typography>
                {data.check_in_notes && (
                  <Box mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">{t('quickCheckIn.detail.checkInNotes') || 'Check-In Notes'}</Typography>
                    <Typography>{data.check_in_notes}</Typography>
                  </Box>
                )}
                {data.check_out_notes && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">{t('quickCheckIn.detail.checkOutNotes') || 'Check-Out Notes'}</Typography>
                    <Typography>{data.check_out_notes}</Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default QuickCheckInDetail;
